import React, { useCallback, useMemo, useState } from 'react';
import { Box, Text } from 'ink';
import { CLI_INPUT_TYPES } from '../../constants';
import type { OnboardingStepId, OnboardingStepState } from '../../types';
import { useInputAction, usePublishInputContext } from '../keymap';
import { useSpinnerFrame } from '../hooks/use_spinner_frame';
import { useTheme } from '../theme';
import type { ThemeTokens } from '../theme';

/**
 * Whether the onboarding card should stay visible for a given input type.
 *
 * The plain text prompt always keeps it; the MCP panel is a deliberate special
 * case (MCP is one of the onboarding steps, so its checklist context stays in
 * view). Every other slash-command panel/dialog (/help, /model, …) hides it.
 */
export const onboardingCardVisibleForInput = (inputType: string): boolean =>
  inputType === CLI_INPUT_TYPES.TEXT || inputType === CLI_INPUT_TYPES.MCP_PANEL;

interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  /** Overrides `title` when the step is completed. */
  completedTitle?: string;
  description: string;
  /** Submitted via `onRun` when this step is focused and Enter is pressed. No-op if absent. */
  command?: string;
}

const STEPS: OnboardingStep[] = [
  {
    id: 'agents-md',
    title: 'Create an AGENTS.md',
    completedTitle: 'AGENTS.md exists in this project',
    description: "Teach Duo your project's conventions & structure",
  },
  {
    id: 'mcp',
    title: 'Connect MCP servers (/mcp)',
    description: 'Give Duo access to your tools, data & services',
    command: '/mcp',
  },
  {
    id: 'skills',
    title: 'Add a local skill',
    description: 'Reusable commands Duo can run on demand',
  },
];

interface StepRowProps {
  step: OnboardingStep;
  focused: boolean;
  completed: boolean;
  loading?: boolean;
}

const stepMarker = (completed: boolean, focused: boolean): string => {
  if (completed) return '✓';
  if (focused) return '→';
  return '○';
};

const stepTitleColor = (
  tokens: ThemeTokens,
  completed: boolean,
  focused: boolean,
): string | undefined => {
  if (focused) return tokens.text.accent;
  if (completed) return tokens.text.subtle;
  return undefined;
};

const StepRow: React.FC<StepRowProps> = ({ step, focused, completed, loading }) => {
  const { tokens } = useTheme();
  const spinnerFrame = useSpinnerFrame('line', Boolean(loading));
  const title = completed && step.completedTitle ? step.completedTitle : step.title;

  return (
    <Box paddingX={1} gap={1} alignItems="center">
      <Text bold={focused} color={loading ? tokens.text.subtle : tokens.text.accent}>
        {loading ? spinnerFrame : stepMarker(completed, focused)}
      </Text>
      <Box flexDirection="column">
        <Text bold={focused && !completed} color={stepTitleColor(tokens, completed, focused)}>
          {title}
        </Text>
        <Text bold={false} color={tokens.text.subtle}>
          {step.description}
        </Text>
      </Box>
    </Box>
  );
};

export interface OnboardingCardProps {
  /** Runs the focused step's command — typically the chat submit handler. */
  onRun: (value: string) => void | Promise<void>;
  /**
   * Whether row selection is active. Set to `false` once the user starts typing:
   * the focus marker clears so Enter clearly submits the message, not a step.
   * The selected index is preserved, so clearing the input restores the highlight.
   * Defaults to `true`.
   */
  active?: boolean;
  /**
   * Completion state per step. Key semantics:
   * - Key absent from map → step is statically incomplete (no async check, shows ○).
   * - Key present with value `'pending'` → check in progress (shows spinner).
   * - `false` → check resolved; step is incomplete (shows ○).
   * - `true` → step is complete (shows ✓, excluded from navigation).
   * Omitting the prop entirely is equivalent to `{}`: no step is loading.
   */
  stepsCompletionMap?: Partial<Record<OnboardingStepId, OnboardingStepState>>;
  /** Number of connected MCP servers, used to pluralize the completed MCP step title. */
  mcpConnectedCount?: number;
}

/**
 * Shown on the empty chat screen to walk a new user through first-time setup.
 * Visual layout mirrors the "Duo CLI" onboarding design (node 328:827).
 *
 * Keyboard navigation (↑/↓ to move, Enter to run) is routed through the central
 * keymap rather than a local key handler: while mounted the card publishes
 * `onboardingActive`, which gates the `onboarding.*` bindings so those keys are
 * claimed here only on the empty screen and only until the user starts typing.
 * Completed steps (value `true` in `stepsCompletionMap`) are excluded from
 * navigation. Enter is a no-op on steps that have no command yet.
 */
export const OnboardingCard: React.FC<OnboardingCardProps> = ({
  onRun,
  active = true,
  stepsCompletionMap: completionMap,
  mcpConnectedCount = 0,
}) => {
  const { tokens } = useTheme();
  const mcpCompletedTitle =
    mcpConnectedCount === 1
      ? '1 MCP server connected'
      : `${mcpConnectedCount} MCP servers connected`;

  const steps = useMemo(
    () => STEPS.map((s) => (s.id === 'mcp' ? { ...s, completedTitle: mcpCompletedTitle } : s)),
    [mcpCompletedTitle],
  );

  const navigableIds = useMemo(
    () => steps.filter((s) => completionMap?.[s.id] !== true).map((s) => s.id),
    [steps, completionMap],
  );

  const isLoading = (id: OnboardingStepId): boolean =>
    completionMap !== undefined && id in completionMap && completionMap[id] === 'pending';
  const isCompleted = (id: OnboardingStepId): boolean => completionMap?.[id] === true;

  const [focusedId, setFocusedId] = useState<OnboardingStepId | null>(
    () => navigableIds[0] ?? null,
  );

  const resolvedFocusedId = navigableIds.includes(focusedId as OnboardingStepId)
    ? (focusedId as OnboardingStepId)
    : (navigableIds[0] ?? null);

  const move = useCallback(
    (direction: number) => {
      setFocusedId((current) => {
        const currentIdx = navigableIds.indexOf(current as OnboardingStepId);
        const fromIdx = currentIdx === -1 ? 0 : currentIdx;
        const nextIdx = Math.max(0, Math.min(navigableIds.length - 1, fromIdx + direction));
        return navigableIds[nextIdx] ?? null;
      });
    },
    [navigableIds],
  );

  usePublishInputContext({ onboardingActive: navigableIds.length > 0 });
  useInputAction('onboarding.prev', () => move(-1));
  useInputAction('onboarding.next', () => move(1));
  useInputAction('onboarding.run', () => {
    const step =
      resolvedFocusedId !== null ? steps.find((s) => s.id === resolvedFocusedId) : undefined;
    return step?.command ? onRun(step.command) : undefined;
  });

  return (
    <Box
      borderStyle="round"
      borderColor={tokens.text.accent}
      paddingX={2}
      paddingY={1}
      marginBottom={1}
      flexDirection="column"
    >
      <Text bold color={tokens.text.accent}>
        Hi!
      </Text>
      <Box marginTop={1}>
        <Text>
          Let&apos;s get you set up — a quick, one-time setup so Duo works smoothly in this project:
        </Text>
      </Box>
      <Box flexDirection="column" marginTop={1}>
        {steps.map((step) => (
          <StepRow
            key={step.id}
            step={step}
            focused={active && step.id === resolvedFocusedId}
            completed={isCompleted(step.id)}
            loading={isLoading(step.id)}
          />
        ))}
      </Box>
      <Box marginTop={1} paddingX={1}>
        <Text dimColor>↑↓ to choose · Enter to run · Type anything to start your question</Text>
      </Box>
    </Box>
  );
};
